import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../../Shared/axiosInstance";

export default function AddProduct() {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    async function loadData() {
      setIsLoadingData(true);
      try {
        const [catRes, brandRes] = await Promise.all([
          axiosInstance.get("/Category"),
          axiosInstance.get("/Brand"),
        ]);
        setCategories(catRes.data?.data || []);
        setBrands(brandRes.data?.data || []);
      } catch (err) {
        console.error(err);
        Swal.fire("خطأ", "فشل في تحميل الأقسام أو العلامات التجارية", "error");
      } finally {
        setIsLoadingData(false);
      }
    }
    loadData();
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      sellPrice: "",
      buyPrice: "",
      categoryId: "",
      brandId: "",
      barcode: "",
    },
    validate: (values) => {
      const errors = {};
      setErrorMsg(null);

      if (!values.name.trim()) errors.name = "اسم المنتج مطلوب";
      if (!values.sellPrice || values.sellPrice <= 0) errors.sellPrice = "سعر البيع يجب أن يكون أكبر من 0";
      if (!values.buyPrice || values.buyPrice <= 0) errors.buyPrice = "سعر الشراء يجب أن يكون أكبر من 0";
      if (!values.categoryId) errors.categoryId = "يرجى اختيار القسم";
      if (!values.brandId) errors.brandId = "يرجى اختيار الماركة";
      if (!values.barcode.trim()) errors.barcode = "الباركود مطلوب";

      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const { data } = await axiosInstance.post("/Product", {
          ...values,
          sellPrice: Number(values.sellPrice),
          buyPrice: Number(values.buyPrice),
        });

        if (data?.status === 200) {
          await Swal.fire({
            title: "تم الإضافة!",
            text: data.message || "تم إضافة المنتج بنجاح",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          navigate("/products");
        }
      } catch (error) {
        console.error("حدث خطأ أثناء إضافة المنتج:", error);
        setErrorMsg(error?.response?.data?.message || "حدث خطأ ما");
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (isLoadingData) {
    return (
      <div className="vh-100 d-flex flex-column align-items-center justify-content-center">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جارٍ تحميل البيانات...
        </p>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px", direction: "rtl" }}>
      <div className="container py-5" style={{ maxWidth: "900px" }}>
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-plus-circle me-2"></i>إضافة منتج جديد
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              أدخل بيانات المنتج الجديد
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/products")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> رجوع
          </button>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <i className="bi bi-exclamation-triangle me-2"></i>
            {errorMsg}
          </div>
        )}

        {/* Form Card */}
        <div style={cardStyle}>
          <form onSubmit={formik.handleSubmit}>
            
            {/* المعلومات الأساسية */}
            <div style={{ marginBottom: "30px" }}>
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-info-circle me-2"></i>المعلومات الأساسية
              </h5>

              <div className="row g-3">
                <div className="col-md-6">
                  <label style={labelStyle}>اسم المنتج *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="أدخل اسم المنتج"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.name && formik.errors.name ? "#dc2626" : "#cbd5e1",
                    }}
                  />
                  {formik.errors.name && formik.touched.name && (
                    <div style={errorStyle}>{formik.errors.name}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label style={labelStyle}>الباركود *</label>
                  <input
                    type="text"
                    name="barcode"
                    placeholder="أدخل الباركود"
                    value={formik.values.barcode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.barcode && formik.errors.barcode ? "#dc2626" : "#cbd5e1",
                    }}
                  />
                  {formik.errors.barcode && formik.touched.barcode && (
                    <div style={errorStyle}>{formik.errors.barcode}</div>
                  )}
                </div>

                <div className="col-12">
                  <label style={labelStyle}>الوصف</label>
                  <textarea
                    name="description"
                    placeholder="أدخل وصف المنتج (اختياري)"
                    rows="3"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    style={{
                      ...inputStyle,
                      resize: "vertical",
                      minHeight: "80px",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* التصنيف */}
            <div style={{ marginBottom: "30px" }}>
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-tags me-2"></i>التصنيف
              </h5>

              <div className="row g-3">
                <div className="col-md-6">
                  <label style={labelStyle}>القسم *</label>
                  <select
                    name="categoryId"
                    value={formik.values.categoryId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.categoryId && formik.errors.categoryId ? "#dc2626" : "#cbd5e1",
                    }}
                  >
                    <option value="">-- اختر القسم --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {formik.errors.categoryId && formik.touched.categoryId && (
                    <div style={errorStyle}>{formik.errors.categoryId}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label style={labelStyle}>الماركة *</label>
                  <select
                    name="brandId"
                    value={formik.values.brandId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.brandId && formik.errors.brandId ? "#dc2626" : "#cbd5e1",
                    }}
                  >
                    <option value="">-- اختر الماركة --</option>
                    {brands.map((br) => (
                      <option key={br.id} value={br.id}>{br.name}</option>
                    ))}
                  </select>
                  {formik.errors.brandId && formik.touched.brandId && (
                    <div style={errorStyle}>{formik.errors.brandId}</div>
                  )}
                </div>
              </div>
            </div>

            {/* الأسعار */}
            <div style={{ marginBottom: "30px" }}>
              <h5 style={sectionTitleStyle}>
                <i className="bi bi-currency-dollar me-2"></i>الأسعار
              </h5>

              <div className="row g-3">
                <div className="col-md-6">
                  <label style={labelStyle}>سعر الشراء *</label>
                  <input
                    type="number"
                    name="buyPrice"
                    placeholder="أدخل سعر الشراء"
                    step="0.01"
                    min="0"
                    value={formik.values.buyPrice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.buyPrice && formik.errors.buyPrice ? "#dc2626" : "#cbd5e1",
                    }}
                  />
                  {formik.errors.buyPrice && formik.touched.buyPrice && (
                    <div style={errorStyle}>{formik.errors.buyPrice}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label style={labelStyle}>سعر البيع *</label>
                  <input
                    type="number"
                    name="sellPrice"
                    placeholder="أدخل سعر البيع"
                    step="0.01"
                    min="0"
                    value={formik.values.sellPrice}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      ...inputStyle,
                      borderColor: formik.touched.sellPrice && formik.errors.sellPrice ? "#dc2626" : "#cbd5e1",
                    }}
                  />
                  {formik.errors.sellPrice && formik.touched.sellPrice && (
                    <div style={errorStyle}>{formik.errors.sellPrice}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex gap-3">
              <button
                type="submit"
                disabled={!(formik.dirty && formik.isValid) || isLoading}
                style={{
                  flex: 1,
                  background:
                    formik.dirty && formik.isValid && !isLoading
                      ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                      : "#94a3b8",
                  color: "#fff",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "18px",
                  cursor:
                    formik.dirty && formik.isValid && !isLoading
                      ? "pointer"
                      : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {isLoading ? (
                  <>
                    <Bars height="25" width="25" color="#fff" ariaLabel="loading" />
                    <span>جارٍ الإضافة...</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle"></i>
                    <span>إضافة المنتج</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/products")}
                style={{
                  flex: 1,
                  background: "#fff",
                  color: "#475569",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "2px solid #cbd5e1",
                  fontWeight: "700",
                  fontSize: "18px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <i className="bi bi-x-circle"></i>
                <span>إلغاء</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ✅ Styles - متطابقة 100% مع صفحة Update
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "32px",
  marginBottom: "20px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
};

const sectionTitleStyle = {
  color: "#1e293b",
  fontSize: "16px",
  fontWeight: "700",
  marginBottom: "16px",
  paddingBottom: "8px",
  borderBottom: "2px solid #e2e8f0",
};

const labelStyle = {
  color: "#475569",
  fontSize: "14px",
  fontWeight: "600",
  marginBottom: "8px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "2px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
  fontWeight: "500",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "13px",
  marginTop: "6px",
  fontWeight: "600",
};
